import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Instagram, Linkedin, Facebook, Mail, MapPin } from 'lucide-react';

const Footer = () => {
  const technologies = [
    { name: 'EndoliftX', path: '/technology/endoliftx' },
    { name: 'Jalupro', path: '/technology/jalupro' },
    { name: 'Aqualyx', path: '/technology/aqualyx' },
    { name: 'Alidya', path: '/technology/alidya' },
    { name: 'MCT', path: '/technology/mct' },
    { name: 'JBP', path: '/technology/jbp' },
    { name: 'Promoitalia', path: '/technology/promoitalia' },
    { name: 'DQ Paris', path: '/technology/dqparis' },
  ];

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/#about' },
    { name: 'Technologies', path: '/#technologies' },
    { name: 'Partner With Us', path: '/#partner' },
    { name: 'Contact', path: '/#contact' },
  ];

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/derma.lis', label: 'Instagram' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Facebook, href: '#', label: 'Facebook' },
  ];

  return (
    <footer className="bg-[#1A1A1A] text-white" data-testid="footer">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-1"
          >
            <Link to="/" className="inline-block mb-6">
              <span className="font-serif text-3xl font-light tracking-tight text-white">
                Dermalis
              </span>
            </Link>
            <p className="body-text text-white/70 mb-8 max-w-xs">
              New Zealand's trusted distributor of advanced aesthetic medical technologies. Serving clinics nationwide from Auckland.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className="w-10 h-10 border border-white/20 flex items-center justify-center hover:bg-white hover:text-[#1A1A1A] transition-all duration-300"
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </motion.div>

          {/* Technologies */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <h4 className="overline text-white/50 mb-6">Technologies</h4>
            <ul className="space-y-4">
              {technologies.map((tech) => (
                <li key={tech.name}>
                  <Link
                    to={tech.path}
                    className="body-text text-white/70 hover:text-white transition-colors duration-300"
                    data-testid={`footer-tech-${tech.name.toLowerCase()}`}
                  >
                    {tech.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <h4 className="overline text-white/50 mb-6">Quick Links</h4>
            <ul className="space-y-4">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="body-text text-white/70 hover:text-white transition-colors duration-300"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <h4 className="overline text-white/50 mb-6">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-white/50 mt-0.5" />
                <a
                  href="mailto:info@dermalis.co.nz"
                  className="body-text text-white/70 hover:text-white transition-colors duration-300"
                  data-testid="footer-email"
                >
                  info@dermalis.co.nz
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-white/50 mt-0.5" />
                <span className="body-text text-white/70">
                  Auckland, New Zealand
                </span>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-white/50">
            &copy; {new Date().getFullYear()} Dermalis. All rights reserved.
          </p>
          <div className="flex gap-8">
            <a href="#" className="text-sm text-white/50 hover:text-white transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="text-sm text-white/50 hover:text-white transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
